import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import AdminLayout from '@/components/AdminLayout';
import { Building2, Search, Plus, Edit, Trash2, Loader2, ChevronLeft, ChevronRight, Settings2, Power, Upload, Download, Eye, MapPin, Mail, Phone, Mailbox } from 'lucide-react';

const EMPTY = { name: '', physical_address: '', postal_address: '', email: '', phone: '', province: '', town: '', is_active: true };
const OPTIONAL_COLS = [
  { key: 'physical_address', label: 'Address' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'postal_address', label: 'Postal Address' },
];

const AdminProviders = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [editing, setEditing] = useState(null); // provider object or 'new'
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewing, setViewing] = useState(null);

  const [showSettings, setShowSettings] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(['name', 'location', 'physical_address', 'phone']);
  const [savingSettings, setSavingSettings] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [province, setProvince] = useState('all');
  const [town, setTown] = useState('all');
  const [filterOpts, setFilterOpts] = useState({ provinces: [], towns: [] });
  const fileInputRef = useRef(null);

  const col = (c) => visibleColumns.includes(c);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '20', include_inactive: 'true' });
      if (search.trim()) params.append('search', search.trim());
      if (province !== 'all') params.append('province', province);
      if (town !== 'all') params.append('town', town);
      const res = await apiClient.get(`/directory/providers?${params.toString()}`);
      setProviders(res.data.providers || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, [page, search, province, town]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiClient.get('/directory/providers/settings').then(r => setVisibleColumns(r.data.visible_columns || [])).catch(() => {});
    apiClient.get('/directory/providers/filters').then(r => setFilterOpts(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Provider name is required'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await apiClient.post('/directory/providers', form);
        toast.success('Provider created');
      } else {
        await apiClient.put(`/directory/providers/${editing.provider_id}`, form);
        toast.success('Provider updated');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save provider');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (p) => {
    try {
      await apiClient.patch(`/directory/providers/${p.provider_id}/status?is_active=${!p.is_active}`);
      toast.success(p.is_active ? 'Provider deactivated' : 'Provider activated');
      load();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/directory/providers/${deleteTarget.provider_id}`);
      toast.success('Provider deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error('Failed to delete provider');
    }
  };

  const toggleColumn = (key) => {
    setVisibleColumns((cols) => cols.includes(key) ? cols.filter(c => c !== key) : [...cols, key]);
  };

  const downloadTemplate = async () => {
    try {
      const res = await apiClient.get('/directory/providers/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'provider_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const exportProviders = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ include_inactive: 'true' });
      if (search.trim()) params.append('search', search.trim());
      if (province !== 'all') params.append('province', province);
      if (town !== 'all') params.append('town', town);
      const res = await apiClient.get(`/directory/providers/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `providers_export_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Providers exported — the file can be re-imported directly.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export providers');
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post(`/directory/providers/import?on_duplicate=skip`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data?.message || 'Import complete');
      const errs = res.data?.errors || [];
      if (errs.length > 0) {
        toast.warning(`${errs.length} row(s) had issues. Downloading error report...`);
        downloadErrorReport(errs);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadErrorReport = (errors) => {
    const header = 'row,name,reason\n';
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = errors.map(e => `${e.row},${escape(e.name)},${escape(e.reason)}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'provider_import_errors.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const optional = visibleColumns.filter(c => OPTIONAL_COLS.some(o => o.key === c));
      await apiClient.put('/directory/providers/settings', { visible_columns: optional });
      toast.success('Directory display settings saved');
      setShowSettings(false);
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <AdminLayout currentPage="providers" title="Provider Directory">
      <div className="space-y-4" data-testid="admin-providers">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search providers..." value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }} data-testid="admin-provider-search" />
            </div>
            <Select value={province} onValueChange={(v) => { setPage(1); setProvince(v); }}>
              <SelectTrigger className="w-40" data-testid="admin-province-filter"><SelectValue placeholder="All Provinces" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Provinces</SelectItem>
                {filterOpts.provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={town} onValueChange={(v) => { setPage(1); setTown(v); }}>
              <SelectTrigger className="w-40" data-testid="admin-town-filter"><SelectValue placeholder="All Towns" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Towns</SelectItem>
                {filterOpts.towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} data-testid="provider-import-input" />
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-1" /> Template
            </Button>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-providers-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {importing ? 'Importing...' : 'Import CSV'}
            </Button>
            <Button variant="outline" onClick={exportProviders} disabled={exporting} data-testid="export-providers-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="directory-settings-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Display Columns
            </Button>
            <Button onClick={openNew} data-testid="add-provider-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Provider
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        ) : providers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No providers yet. Click &quot;Add Provider&quot; to create one.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {providers.map((p) => (
              <Card key={p.provider_id} data-testid={`admin-provider-${p.provider_id}`}>
                <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{p.name}</p>
                      {!p.is_active && <Badge variant="outline" className="text-red-600 border-red-300">Inactive</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {[p.town, p.province].filter(Boolean).join(', ') || '—'}
                      {col('phone') && p.phone && ` · ${p.phone}`}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setViewing(p)} title="View details" data-testid={`view-provider-${p.provider_id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(p)} title={p.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-provider-${p.provider_id}`}>
                      <Power className={`w-4 h-4 ${p.is_active ? 'text-green-600' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-provider-${p.provider_id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(p)} data-testid={`delete-provider-${p.provider_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {total > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Page {page} of {totalPages} · {total} providers</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="w-4 h-4" /> Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next <ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="provider-form-dialog">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Add Provider' : 'Edit Provider'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Provider Name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="provider-name-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Physical Address</Label>
              <Input value={form.physical_address} onChange={(e) => setForm({ ...form, physical_address: e.target.value })} data-testid="provider-physical-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Postal Address</Label>
              <Input value={form.postal_address} onChange={(e) => setForm({ ...form, postal_address: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Province</Label>
              <Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="provider-province-input" />
            </div>
            <div>
              <Label>Town</Label>
              <Input value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} data-testid="provider-town-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-provider-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {saving ? 'Saving...' : 'Save Provider'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="provider-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" /> {viewing?.name}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {viewing.is_active
                  ? <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">Active</Badge>
                  : <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50">Inactive</Badge>}
              </div>
              <div className="space-y-3 text-sm">
                {col('physical_address') && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Physical Address</p>
                    <p className="font-medium">{viewing.physical_address || '—'}</p>
                    <p className="text-muted-foreground">{[viewing.town, viewing.province].filter(Boolean).join(', ') || ''}</p>
                  </div>
                </div>
                )}
                {!col('physical_address') && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Location</p>
                    <p className="font-medium">{[viewing.town, viewing.province].filter(Boolean).join(', ') || '—'}</p>
                  </div>
                </div>
                )}
                {col('postal_address') && (
                <div className="flex items-start gap-3">
                  <Mailbox className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Postal Address</p>
                    <p className="font-medium">{viewing.postal_address || '—'}</p>
                  </div>
                </div>
                )}
                {col('phone') && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Phone</p>
                    <p className="font-medium">{viewing.phone || '—'}</p>
                  </div>
                </div>
                )}
                {col('email') && (
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Email</p>
                    <p className="font-medium">{viewing.email || '—'}</p>
                  </div>
                </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
            <Button onClick={() => { const p = viewing; setViewing(null); openEdit(p); }} data-testid="view-to-edit-btn">
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="provider-delete-dialog">
          <DialogHeader><DialogTitle>Delete Provider</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure you want to permanently delete <span className="font-medium">{deleteTarget?.name}</span>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="confirm-delete-provider-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Display Columns Settings */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent data-testid="directory-settings-dialog">
          <DialogHeader><DialogTitle>Directory Display Columns</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Name and Location are always shown. Choose additional columns to display.</p>
            {OPTIONAL_COLS.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={visibleColumns.includes(c.key)} onCheckedChange={() => toggleColumn(c.key)} data-testid={`col-toggle-${c.key}`} />
                {c.label}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingSettings} data-testid="save-directory-settings-btn">
              {savingSettings ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProviders;
